/* eslint-disable indent */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/camelcase */
import { Chip, Grid, makeStyles, Typography } from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';
import clsx from 'clsx';
import Button from 'components/Button';
import PageTitle from 'components/PageTitle';
import RefreshButton from 'components/RefreshButton';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import Permission from 'components/ValidPermission';
import { Query, useTableFilters } from 'hooks/useTableFilters';
import i18n from 'i18n';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import {
  editOperatorTokenRequest,
  getOperatorTokensRequest,
  newOperatorTokenRequest,
  removeOperatorTokenRequest,
} from 'modules/operators/actions';
import {
  GetOperatorFields,
  ListOperatorTokensDataProps,
  OperatorTokenFields,
  OperatorTokensProps,
} from 'modules/operators/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import ApplicationState from 'store/interfaces';
import { cyrb53Hash, formatDateWithMoment } from 'utils/helpers';
import { inAdminView, inOperatorView, inSuperAdminView } from 'utils/role';
import FormToken from './FormToken';
import styles from '../styles';

const useStyles = makeStyles(styles);

interface TokenAPISectionProps {
  companySelected: CompanyData | null;
  operator: GetOperatorFields | null;
  operator_uuid: string;
  operatorTokens: ListOperatorTokensDataProps;
  permissions?: PermissionsData;
  role?: RolesEnum;
  editOperatorTokenRequest: (props: {
    operatorUuid: string;
    tokenUuid: string;
    values: OperatorTokenFields;
    query: Query;
  }) => void;
  getOperatorTokensRequest: (props: {
    operatorUuid: string;
    query: Query;
  }) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  newOperatorTokenRequest: (props: {
    operatorUuid: string;
    values: OperatorTokenFields;
    query: Query;
    operatorEmail?: string;
  }) => Promise<void>;
  removeOperatorTokenRequest: (props: {
    operatorUuid: string;
    tokenUuid: string;
    query: Query;
  }) => void;
}

const TokenAPISection = ({
  companySelected,
  operator,
  operator_uuid,
  operatorTokens,
  permissions,
  role,
  editOperatorTokenRequest,
  getOperatorTokensRequest,
  modalClose,
  modalOpen,
  newOperatorTokenRequest,
  removeOperatorTokenRequest,
}: TokenAPISectionProps) => {
  const classes = useStyles();
  const { query, handleLimitChange, handlePageChange } = useTableFilters();
  const { id } = useParams<{ id: string }>();

  const [refresh, setRefresh] = React.useState(false);

  const getPermissionListToken = (): Array<keyof PermissionsData> => {
    const permissions: Array<keyof PermissionsData> = [
      'list_all_operator_token',
    ];

    if (id !== operator_uuid) return permissions;
    return permissions.concat(['list_self_operator_token']);
  };

  const getPermissionCreateToken = (): Array<keyof PermissionsData> => {
    const permissions: Array<keyof PermissionsData> = ['create_api_token'];

    if (id !== operator_uuid) return permissions;
    return permissions.concat(['create_self_api_token']);
  };

  const getPermissionDeleteToken = (): Array<keyof PermissionsData> => {
    const permissions: Array<keyof PermissionsData> = ['delete_api_token'];

    if (id !== operator_uuid) return permissions;
    return permissions.concat(['delete_self_api_token']);
  };

  const getPermissionEditToken = (): Array<keyof PermissionsData> => {
    const permissions: Array<keyof PermissionsData> = ['edit_api_token'];

    if (id !== operator_uuid) return permissions;
    return permissions.concat(['edit_self_api_token']);
  };

  const getRequiredActionsPermissions = (): Array<keyof PermissionsData> => {
    const deletePermissions = getPermissionDeleteToken();
    const editPermissions = getPermissionEditToken();

    return deletePermissions.concat(editPermissions);
  };

  const headers: Array<TableCellHeaderProps & { hidden?: boolean }> = [
    { key: 'description', i18n: 'DESCRIPTION_KEY' },
    { key: 'creation_date', i18n: 'CREATION_DATE_KEY', centerTitle: true },
    { key: 'expiration_date', i18n: 'EXPIRATION_DATE_KEY', centerTitle: true },
    { key: 'partner', i18n: 'PARTNER_KEY' },
    {
      key: 'filter_using_ips',
      i18n: 'OPERATORS_PAGE.REQUIRES_IP_VALIDATION',
      centerTitle: true,
    },
    { key: 'ips', i18n: 'IPs' },
    {
      key: 'actions',
      i18n: 'ACTIONS_KEY',
      centerTitle: true,
      hidden:
        !!permissions &&
        getRequiredActionsPermissions().every(
          permission => !permissions[permission]
        ),
    },
  ].filter(item => !item?.hidden);

  const getTokens = (refresh = false) => {
    getOperatorTokensRequest({
      operatorUuid: id,
      query,
    });
    setRefresh(refresh);
  };

  React.useEffect(() => {
    getTokens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, getOperatorTokensRequest]);

  const handleOpenFormToken = (item?: OperatorTokensProps) =>
    modalOpen({
      overrideClasses: {
        content: {
          padding: 12,
          maxHeight: 648,
          overflowX: 'auto',
          fontSize: '12px !important',
        },
      },
      title: item
        ? i18n.t('OPERATORS_PAGE.EDIT_TOKEN_KEY')
        : i18n.t('OPERATORS_PAGE.ADD_TOKEN_KEY'),
      content: (
        <FormToken
          onCancel={modalClose}
          defaultValues={item}
          onSubmit={(values: OperatorTokenFields) =>
            item
              ? editOperatorTokenRequest({
                  operatorUuid: id,
                  tokenUuid: item.token_uuid,
                  values,
                  query,
                })
              : newOperatorTokenRequest({
                  operatorUuid: id,
                  operatorEmail: operator?.email,
                  values,
                  query,
                })
          }
        />
      ),
    });

  const handleRemoveToken = (token: OperatorTokensProps) =>
    modalOpen({
      title: i18n.t('OPERATORS_PAGE.REMOVE_TOKEN_TITLE_KEY'),
      content: i18n.t('OPERATORS_PAGE.REMOVE_TOKEN_DESCRIPTION_KEY', {
        name: token.description,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              removeOperatorTokenRequest({
                operatorUuid: id,
                tokenUuid: token.token_uuid,
                query,
              });
              modalClose();
            }}
          >
            {i18n.t('REMOVE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  const getMenuActionItems = (item: OperatorTokensProps) =>
    [
      {
        key: 'delete_token',
        label: 'DELETE_KEY',
        onClick: () => handleRemoveToken(item),
        hidden:
          !!permissions &&
          getPermissionDeleteToken().every(
            permission => !permissions[permission]
          ),
      },
      {
        key: 'edit_token',
        label: 'EDIT_KEY',
        onClick: () => handleOpenFormToken(item),
        hidden:
          !!permissions &&
          getPermissionEditToken().every(
            permission => !permissions[permission]
          ),
      },
    ].filter(action => !action.hidden);

  return (
    <Permission value={getPermissionListToken()}>
      <Grid
        container
        direction="row"
        alignItems="center"
        justify="space-between"
        className={classes.content}
      >
        <PageTitle title={i18n.t('OPERATORS_PAGE.API_INTEGRATION_IPS_KEY')} />

        <Grid className={classes.addButtonContainer}>
          <RefreshButton
            id="btn-refresh"
            onClick={() => {
              getTokens(true);
            }}
            animate={operatorTokens?.loading}
            className={classes.additionalButtons}
            disabled={operatorTokens?.loading}
          />

          <Permission value={getPermissionCreateToken()}>
            <Button
              id="add-token"
              variant="outlined"
              size="large"
              className={clsx(classes.buttonDefault, {
                [classes.buttonSuperAdmin]: inSuperAdminView(
                  role,
                  companySelected
                ),
                [classes.buttonAdmin]: inAdminView(role, companySelected),
                [classes.buttonOperator]: inOperatorView(role, companySelected),
              })}
              startIcon={<AddIcon />}
              onClick={() => handleOpenFormToken()}
            >
              {i18n.t('OPERATORS_PAGE.ADD_TOKEN_KEY')}
            </Button>
          </Permission>
        </Grid>
      </Grid>

      <TableItems
        header={headers.map(item => (
          <TableCell key={item?.key} centerTitle={item?.centerTitle}>
            {i18n.t(item?.i18n)}
          </TableCell>
        ))}
        renderItems={(item: OperatorTokensProps) => (
          <TableRow key={item?.token_uuid}>
            <TableCell>{item?.description}</TableCell>
            <TableCell>
              <Grid container justify="center">
                {formatDateWithMoment(item?.creation_date)}
              </Grid>
            </TableCell>
            <TableCell>
              <Grid container justify="center">
                {formatDateWithMoment(item?.expiration_date)}
              </Grid>
            </TableCell>
            <TableCell>{item?.partner.name}</TableCell>
            <TableCell>
              <Grid container justify="center">
                {i18n.t(item?.filter_using_ips ? 'YES_KEY' : 'NO_KEY')}
              </Grid>
            </TableCell>
            <TableCell
              className={classes.chipContainer}
              ellipsis={!!item?.valid_ips}
            >
              {item?.valid_ips &&
                item?.valid_ips
                  .split(',')
                  .map(label => (
                    <Chip
                      variant="outlined"
                      key={cyrb53Hash(label)}
                      label={label}
                    />
                  ))}
            </TableCell>
            <Permission value={getRequiredActionsPermissions()}>
              <TableCell textAlignCenter>
                <MenuActions>
                  {getMenuActionItems(item)?.length > 0 ? (
                    getMenuActionItems(item)?.map(action => (
                      <MenuActionItem
                        key={action?.key}
                        id={action?.key}
                        onClick={action?.onClick}
                      >
                        {i18n.t(action?.label)}
                      </MenuActionItem>
                    ))
                  ) : (
                    <MenuActionItem>
                      {i18n.t('NO_ACTIONS_AVAILABLE_KEY')}
                    </MenuActionItem>
                  )}
                </MenuActions>
              </TableCell>
            </Permission>
          </TableRow>
        )}
        data={operatorTokens}
        isLoading={!refresh && operatorTokens.loading}
        onChangePage={handlePageChange}
        onChangeLimit={handleLimitChange}
        notFoundText={
          <Typography className={classes.notFoundText}>
            {i18n.t('OPERATORS_PAGE.APIS_OPERATOR_NOT_FOUND')}
          </Typography>
        }
        colCount={7}
      />
    </Permission>
  );
};

const mapStateToProps = ({
  authenticate: { data, companySelected },
  operators: { operatorDetails, operatorTokens },
}: ApplicationState) => ({
  companySelected,
  operator: operatorDetails,
  operatorTokens,
  operator_uuid: data?.operator_uuid!,
  permissions: data?.permissions,
  role: data?.role,
});

export default connect(mapStateToProps, {
  editOperatorTokenRequest,
  getOperatorTokensRequest,
  modalOpen,
  modalClose,
  newOperatorTokenRequest,
  removeOperatorTokenRequest,
})(TokenAPISection);
